import { useState, useCallback } from 'react'
import { lightTheme } from '@/customTheme'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@/store/store'
import { selectUserIsPremium } from '@/store/slices/user/selectors'
import { setOpenModalTryPremium, setUser, setUserIsPremium, setUserIsSuper } from '@/store/slices/user/actions'
import MiniMenu from './MiniMenu'
import { SignInManager } from './SignInManager'
import { customAmplitude } from '@/utils/customAmplitude'
import { useTranslation } from 'react-i18next'
import ModalCustom from '../../Modal/Modal'
import { handleContactUs } from '@/utils/contactUs'
import { NavbarButtonBaseProps } from '../navbarConfig'
import { useLocation } from 'react-router-dom'
import { selectTemplateConfig } from '@/store/slices/templates/selectors'
import { ShowModalType } from '@/scenes/engine/common/constants'
import * as amplitude from '@amplitude/analytics-browser'
import * as Sentry from '@sentry/react'
import useModal from '@/hooks/useModal'
import { FloatingInspectorButtonIcon } from '../../FloatingInspectorButton/FloatingInspectorButtons'
import WrapPopover from '../../FloatingInspectorButton/SharedComponents/Popover/WrapPopover'
import { VerticalListButton } from '../../FloatingInspectorButton/SharedComponents/Popover/VerticalListButton'
import { HorizontalSeparator } from '../../FloatingInspectorButton/SharedComponents/Popover/HorizontalSeparator'

interface UserMenuPopoverProps extends NavbarButtonBaseProps {
  hideTryRemoveObjects?: boolean
  onSignOut?: () => void
}

function UserMenuPopover({ disabled = false, hideTryRemoveObjects = false, onSignOut }: UserMenuPopoverProps) {
  const { t } = useTranslation()
  const [isOpenModalManage, setIsOpenModalManage] = useState(false)

  return (
    <>
      <FloatingInspectorButtonIcon
        onClick={() => {}}
        disabled={disabled}
        disabledStyle="transparentBackground"
        borderRadiusStyle="rounded"
        showArrow="down"
        style={{ padding: '4px 8px 4px 4px', gap: '2px', height: '40px' }}
        icon={
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: disabled ? lightTheme.colors.grayScale50 : lightTheme.colors.blackGray,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: disabled ? lightTheme.colors.grayScale200 : lightTheme.colors.white,
              textTransform: 'uppercase',
              flexShrink: 0,
            }}
          >
            <span
              style={{
                ...lightTheme.typography.Small16Bold,
                fontWeight: 400,
                textTransform: 'uppercase',
                lineHeight: 1,
                color: disabled ? lightTheme.colors.grayScale200 : lightTheme.colors.white,
                transform: 'translateY(1px)',
              }}
            >
              {SignInManager.getInstance().email ? SignInManager.getInstance()?.email[0] ?? 'B' : ''}
            </span>
          </div>
        }
        popoverContent={(buttonRef, closePopover) => (
          <UserMenuContent
            closeMenu={closePopover}
            setIsOpenModalManage={setIsOpenModalManage}
            buttonRef={buttonRef}
            hideTryRemoveObjects={hideTryRemoveObjects}
            onSignOut={onSignOut}
          />
        )}
      />
      <ModalCustom
        isOpen={isOpenModalManage}
        onClose={setIsOpenModalManage}
        content={{
          header: t('Manage subscription'),
          desc: t(
            'To manage your subscription, open the Bazaart app on your mobile device and go to settings.'
          ),
        }}
        action={{
          primary: {
            name: t('Ok'),
            handle: () => setIsOpenModalManage(false),
          },
        }}
      />
    </>
  )
}

export default UserMenuPopover

function UserMenuContent({
  closeMenu,
  setIsOpenModalManage,
  buttonRef,
  hideTryRemoveObjects = false,
  onSignOut,
}: {
  closeMenu: () => void
  setIsOpenModalManage: (v: boolean) => void
  buttonRef: React.RefObject<HTMLElement>
  hideTryRemoveObjects?: boolean
  onSignOut?: () => void
}) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { setShowModalType } = useModal()
  const hasPremium = useSelector(selectUserIsPremium)
  const location = useLocation()
  const templateConfig = useSelector(selectTemplateConfig)

  const [isOpenContactMenu, setIsOpenContactMenu] = useState(false)
  const [isOpenSelectLanguage, setIsOpenSelectLanguage] = useState(false)
  const [isOpenManageSubscription, setIsOpenManageSubscription] = useState(false)
  const [isOpenAIUsage, setIsOpenAIUsage] = useState(false)

  const isSubMenuOpen = isOpenContactMenu || isOpenSelectLanguage || isOpenManageSubscription || isOpenAIUsage

  const signInManager = SignInManager.getInstance()
  const userEmail = signInManager.email
  const platformType = signInManager.platformType
  const connectedWithLabel = platformType
    ? t(`Connected with ${platformType.charAt(0).toUpperCase() + platformType.slice(1)}`)
    : null

  const handleSignOut = useCallback(() => {
    const isProject = templateConfig?.draftGuid && !templateConfig?.templateId
    if (isProject && !location.pathname.includes('create')) {
      setShowModalType(ShowModalType.CONFIRM_SIGN_OUT)
    } else {
      SignInManager.getInstance().logout()
      dispatch(setUser(null))
      dispatch(setUserIsPremium(false))
      dispatch(setUserIsSuper(false))
      setShowModalType(null)
      onSignOut?.()
      dispatch(setOpenModalTryPremium({ isOpen: false, source: null, callback: null, opened: false }))
      const identifyEvent = new amplitude.Identify()
      identifyEvent.set('isSignup', false)
      identifyEvent.set('isPremium', false)
      identifyEvent.set('isSuper', false)
      amplitude.identify(identifyEvent)
      Sentry.setUser(null)
      setShowModalType(ShowModalType.SIGN_IN_1ST)
    }
  }, [location, templateConfig, dispatch, setShowModalType, onSignOut])

  return (
    <WrapPopover padding={16} width={292} gap={0} placement="bottomRight" buttonRef={buttonRef}>
      {!isSubMenuOpen && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'row',
              gap: '8px',
              padding: '24px 0px 16px 8px',
            }}
          >
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: lightTheme.colors.blackGray,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: lightTheme.colors.white,
              textTransform: 'uppercase',
            }}
          >
            {userEmail ? userEmail[0] ?? '?' : ''}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              maxWidth: '208px',
            }}
          >
            <p
              style={{
                ...lightTheme.typography.Small14Semibold,
                color: lightTheme.colors.blackGray,
                lineHeight: '16px',
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {userEmail
                ? userEmail.split('@')[0]
                : 'Default'}
            </p>
            <p
              style={{
                ...lightTheme.typography.Small14regular,
                color: 'rgba(153, 153, 153, 1)',
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {userEmail ?? 'Default'}
            </p>
            {connectedWithLabel && (
              <p
                style={{
                  ...lightTheme.typography.LabelSmall,
                  lineHeight: '14px',
                  color: 'rgba(153, 153, 153, 1)',
                  margin: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {connectedWithLabel}
              </p>
            )}
          </div>
        </div>
        <HorizontalSeparator />
        </>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          marginTop: isSubMenuOpen ? 0 : '8px',
        }}
      >
        {!isSubMenuOpen && !hasPremium && (
          <VerticalListButton
            variant="menu"
            text={t('Upgrade to Premium')}
            onClick={() => {
              customAmplitude('Premium Prompt', {
                Source: 'BtSubscriptionSettings',
                Type: 'Standard',
              })
              // @ts-ignore
              window.dataLayer.push({
                event: 'premium_prompt',
                ...{
                  Source: 'BtSubscriptionSettings',
                  Type: 'Standard',
                },
              })
              dispatch(
                setOpenModalTryPremium({
                  isOpen: true,
                  source: 'BtSubscriptionSettings',
                })
              )
              closeMenu()
            }}
          />
        )}
        <MiniMenu
          handleClickContact={handleContactUs}
          isOpenContactMenu={isOpenContactMenu}
          setIsOpenContactMenu={setIsOpenContactMenu}
          isOpenSelectLanguage={isOpenSelectLanguage}
          setIsOpenSelectLanguage={setIsOpenSelectLanguage}
          isOpenManageSubscription={isOpenManageSubscription}
          setIsOpenManageSubscription={setIsOpenManageSubscription}
          isOpenAIUsage={isOpenAIUsage}
          setIsOpenAIUsage={setIsOpenAIUsage}
          setIsOpenModalManage={setIsOpenModalManage}
          hasPremium={hasPremium}
          closeMenu={closeMenu}
          hideTryRemoveObjects={hideTryRemoveObjects}
        />
        {!isSubMenuOpen && (
          <VerticalListButton
            variant="menu"
            text={t('Sign out')}
            onClick={() => {
              handleSignOut()
              closeMenu()
            }}
          />
        )}
      </div>
    </WrapPopover>
  )
}
